import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";
import {
  Button, DatePicker, Input, Modal, Select, Tooltip, Upload,
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  createRcaTracking,
  fetchRcaTracking,
  patchRcaTracking,
} from "../../../actions/qc";
import { getUser } from "../../../utils/helpers";
import RcaDocuments from "../QC/RcaDocuments";
import {
  RCA_UPLOAD_ACCEPT,
  uploadRcaDocument,
  validateRcaDocumentFile,
} from "../QC/utils";
import RcaRelatedWorkorderTable from "./RcaRelatedWorkorderTable";
import { loadDataOpsAssigneeSelectOptions } from "./utils/utils";
import {
  buildRcaTrackingSavePayload,
  getRcaSubmitDisabledReason,
  RCA_DETAIL_STATUS_OPTIONS,
  RCA_STATUS,
  resolveRcaDetailFormStatusValue,
} from "./utils/rcaUtils";
import { fetchRcaRelatedWorkorderRows } from "./utils/rcaRelatedInfoUtils";
import {
  buildAddRcaSubmitModalSubheadingParts,
  isAddRcaTestSampleExisting,
} from "./utils/addRcaTestSamplesUtils";
import "../QC/MarkSampleQCModal/components/QCFormSection.css";
import "../ReportDelay/UpdateDelayReportModal.css";
import "../ReportDelay/ReportDelayHistory.css";

const { TextArea } = Input;

const parseExpectedCompleteDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const populateFormFromRcaRow = (row, setters) => {
  if (!row) {
    return;
  }
  setters.setRootCauses(row.rca_description ?? row.root_causes ?? row.root_cause ?? "");
  setters.setConclusions(row.conclusion ?? row.conclusions ?? "");
  setters.setStatus(resolveRcaDetailFormStatusValue(row));
  setters.setOnHoldReason(row.onhold_reason ?? row.on_hold_reason ?? "");
  setters.setAssignedTo(row.assigned_to ?? undefined);
  setters.setExpectedCompleteDate(parseExpectedCompleteDate(row.expected_complete_date));
  setters.setExistingS3Keys(Array.isArray(row.s3_keys) ? row.s3_keys : []);
  setters.setPendingRcaS3Keys([]);
};

/**
 * RCA details modal — create (Add RCA) or edit (tracking history).
 */
export default function AddRcaSubmitModal({
  open,
  selectedRows = [],
  subtitleContext = null,
  onCancel,
  onSubmitted,
}) {
  const [rootCauses, setRootCauses] = useState("");
  const [conclusions, setConclusions] = useState("");
  const [status, setStatus] = useState(undefined);
  const [onHoldReason, setOnHoldReason] = useState("");
  const [assignedTo, setAssignedTo] = useState(undefined);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [expectedCompleteDate, setExpectedCompleteDate] = useState(null);
  const [existingS3Keys, setExistingS3Keys] = useState([]);
  const [pendingRcaS3Keys, setPendingRcaS3Keys] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [relatedWorkorderRows, setRelatedWorkorderRows] = useState([]);
  const [relatedWorkordersLoading, setRelatedWorkordersLoading] = useState(false);

  const selectedRow = selectedRows[0] ?? null;
  const selectedRowKey = selectedRow?.tests_in_sample_pk ?? selectedRow?.rca_id ?? selectedRow?.key ?? "";
  const tisPk = selectedRow?.tests_in_sample_pk ?? selectedRow?.tis_pk;
  const isExistingRca = useMemo(
    () => (selectedRow ? isAddRcaTestSampleExisting(selectedRow) : false),
    [selectedRow],
  );

  const subheadingParts = useMemo(() => {
    if (!selectedRow && !subtitleContext) {
      return [];
    }
    return buildAddRcaSubmitModalSubheadingParts({
      ...(subtitleContext || {}),
      ...(selectedRow || {}),
    });
  }, [selectedRow, subtitleContext]);

  const requiresCompletedFields = status === RCA_STATUS.COMPLETED;
  const requiresOnHoldReason = status === RCA_STATUS.ON_HOLD;

  useEffect(() => {
    if (!open) {
      return () => {};
    }
    const row = selectedRows[0];
    const setters = {
      setRootCauses,
      setConclusions,
      setStatus,
      setOnHoldReason,
      setAssignedTo,
      setExpectedCompleteDate,
      setExistingS3Keys,
      setPendingRcaS3Keys,
    };

    const loadUsers = async () => {
      setLoadingUsers(true);
      const response = await loadDataOpsAssigneeSelectOptions();
      setLoadingUsers(false);
      if (!response.success) {
        toast.error(response.message || "Failed to load users");
        return;
      }
      setAssigneeOptions(response.options);
    };

    const loadForm = async () => {
      populateFormFromRcaRow(row, setters);

      if (!tisPk) {
        return;
      }

      const rcaPk = row?.rca_pk ?? row?.rca_id ?? row?.id;
      const response = await fetchRcaTracking({
        tis_pk: tisPk,
        filter: "ALL",
      });
      if (!response.success) {
        toast.error(response.message || "Failed to load RCA details");
        return;
      }
      const records = Array.isArray(response.result) ? response.result : [];
      let match = null;
      if (rcaPk) {
        match = records.find(
          (item) => String(item.rca_pk ?? item.id ?? item.rca_id) === String(rcaPk),
        );
      } else {
        match = records.find(
          (item) => Number(item.rca_status) === Number(row.rca_status),
        ) ?? records[0];
      }
      if (match) {
        populateFormFromRcaRow(match, setters);
      } else {
        populateFormFromRcaRow(row, setters);
      }
    };

    loadUsers();

    if (isAddRcaTestSampleExisting(row) && tisPk) {
      loadForm();
    } else {
      populateFormFromRcaRow(row, setters);
    }

    let canceled = false;
    const loadRelatedWorkorders = async () => {
      if (!row) {
        setRelatedWorkorderRows([]);
        return;
      }
      setRelatedWorkordersLoading(true);
      setRelatedWorkorderRows([]);
      const { success, rows, message } = await fetchRcaRelatedWorkorderRows(row);
      if (canceled) {
        return;
      }
      if (!success) {
        if (message) {
          toast.warn(message);
        }
        setRelatedWorkorderRows([]);
      } else {
        setRelatedWorkorderRows(rows);
      }
      setRelatedWorkordersLoading(false);
    };
    loadRelatedWorkorders();

    return () => {
      canceled = true;
    };
  }, [open, selectedRowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedS3Keys = useMemo(
    () => [...existingS3Keys, ...pendingRcaS3Keys],
    [existingS3Keys, pendingRcaS3Keys],
  );

  const canSubmit = useMemo(() => {
    if (!selectedRow || !expectedCompleteDate) {
      return false;
    }
    if (expectedCompleteDate.isBefore(dayjs(), "day")) {
      return false;
    }
    if (status == null || status === "") {
      return false;
    }
    if (status === RCA_STATUS.ON_HOLD && !onHoldReason.trim()) {
      return false;
    }
    if (status === RCA_STATUS.COMPLETED) {
      if (!rootCauses.trim() || !conclusions.trim() || !assignedTo) {
        return false;
      }
      if (displayedS3Keys.length === 0) {
        return false;
      }
    }
    return true;
  }, [
    selectedRow,
    rootCauses,
    conclusions,
    expectedCompleteDate,
    assignedTo,
    status,
    onHoldReason,
    displayedS3Keys,
  ]);

  const submitDisabledReason = useMemo(() => {
    if (canSubmit && !submitting && uploadingCount === 0) {
      return null;
    }
    return getRcaSubmitDisabledReason({
      selectedRow,
      status,
      expectedCompleteDate,
      rootCauses,
      conclusions,
      assignedTo,
      onHoldReason,
      submitting,
      uploadingCount,
      s3Keys: displayedS3Keys,
    });
  }, [
    canSubmit,
    selectedRow,
    status,
    expectedCompleteDate,
    rootCauses,
    conclusions,
    assignedTo,
    onHoldReason,
    submitting,
    uploadingCount,
    displayedS3Keys,
  ]);

  const beforeUpload = (file) => {
    const validation = validateRcaDocumentFile(file);
    if (!validation.valid) {
      toast.error(validation.message);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customUpload = async ({ file, onSuccess, onError }) => {
    if (!tisPk) {
      toast.error("Missing test id for document upload.");
      if (onError) {
        onError(new Error("Missing tis_pk"));
      }
      return;
    }
    setUploadingCount((count) => count + 1);
    const result = await uploadRcaDocument(file, tisPk);
    setUploadingCount((count) => Math.max(0, count - 1));
    if (!result.success) {
      toast.error(result.message);
      if (onError) {
        onError(new Error(result.message));
      }
      return;
    }
    setPendingRcaS3Keys((prevKeys) => [...prevKeys, result.path]);
    if (onSuccess) {
      onSuccess(result);
    }
  };

  const removeRcaDocument = (s3Key) => {
    setPendingRcaS3Keys((prevKeys) => prevKeys.filter((key) => key !== s3Key));
    setExistingS3Keys((prevKeys) => prevKeys.filter((key) => key !== s3Key));
  };

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedRow || !tisPk) {
      if (!tisPk) {
        toast.error("Missing test id for this RCA.");
      }
      return;
    }

    const user = getUser();
    const formattedExpectedDate = expectedCompleteDate.format("YYYY-MM-DD");
    const rcaPk = selectedRow.rca_pk ?? selectedRow.rca_id ?? selectedRow.id;
    if (isExistingRca && !rcaPk) {
      toast.error("Missing RCA id for update.");
      return;
    }

    const savePayload = buildRcaTrackingSavePayload({
      isEdit: isExistingRca,
      rcaPk,
      tisPk,
      userId: user.id,
      rcaStatus: status,
      assignedTo,
      expectedCompleteDate: formattedExpectedDate,
      s3Keys: displayedS3Keys,
      rootCauses,
      conclusions,
      onHoldReason,
    });

    setSubmitting(true);

    const response = isExistingRca
      ? await patchRcaTracking(savePayload)
      : await createRcaTracking(savePayload);

    setSubmitting(false);

    if (!response.success) {
      toast.error(response.message || (isExistingRca ? "Failed to update RCA" : "Failed to create RCA"));
      return;
    }

    toast.success(
      response.message || (isExistingRca ? "RCA updated successfully" : "RCA created successfully"),
    );
    if (onSubmitted) {
      onSubmitted();
    }
  }, [
    canSubmit,
    selectedRow,
    isExistingRca,
    tisPk,
    expectedCompleteDate,
    displayedS3Keys,
    assignedTo,
    rootCauses,
    conclusions,
    status,
    onHoldReason,
    onSubmitted,
  ]);

  const submitDisabled = !canSubmit || submitting || uploadingCount > 0;
  const submitButton = (
    <Button
      type="primary"
      disabled={submitDisabled}
      loading={submitting}
      onClick={handleSubmit}
    >
      Submit
    </Button>
  );

  const modalFooter = (
    <div className="UpdateDelayReportModal__Footer">
      <Button onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      {submitDisabled && submitDisabledReason ? (
        <Tooltip title={submitDisabledReason}>
          <span className="UpdateDelayReportModal__SubmitTooltipTarget">
            {submitButton}
          </span>
        </Tooltip>
      ) : (
        submitButton
      )}
    </div>
  );

  return (
    <Modal
      centered
      open={open}
      title="RCA details"
      onCancel={onCancel}
      width={800}
      destroyOnClose
      maskClosable={false}
      className="UpdateDelayReportModal"
      footer={modalFooter}
    >
      <div className="UpdateDelayReportModal__Body">
        {subheadingParts.length > 0 && (
          <div className="ReportDelayHistory__Subheading">
            {subheadingParts.map((part, index) => (
              <React.Fragment key={part.key}>
                {index > 0 && (
                  <span className="ReportDelayHistory__SubheadingSeparator">|</span>
                )}
                <span>{part.text}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="UpdateDelayReportModal__Field">
          <label>
            Status
            <span className="QCFormSection__RequiredMark"> *</span>
          </label>
          <Select
            placeholder="Select status"
            options={RCA_DETAIL_STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
          />
        </div>

        <div className="UpdateDelayReportModal__Grid">
          <div className="UpdateDelayReportModal__Field">
            <label>
              Root Cause(s)
              {requiresCompletedFields && (
                <span className="QCFormSection__RequiredMark"> *</span>
              )}
            </label>
            <TextArea
              rows={5}
              value={rootCauses}
              onChange={(event) => setRootCauses(event.target.value)}
              placeholder="Describe root cause(s)"
            />
          </div>
          <div className="UpdateDelayReportModal__Field">
            <label>
              Conclusions
              {requiresCompletedFields && (
                <span className="QCFormSection__RequiredMark"> *</span>
              )}
            </label>
            <TextArea
              rows={5}
              value={conclusions}
              onChange={(event) => setConclusions(event.target.value)}
              placeholder="Summarize conclusions"
            />
          </div>
        </div>

        <div className="UpdateDelayReportModal__Field">
          <label>
            Assign To
            {requiresCompletedFields && (
              <span className="QCFormSection__RequiredMark"> *</span>
            )}
          </label>
          <Select
            showSearch
            loading={loadingUsers}
            placeholder="Select assignee"
            options={assigneeOptions}
            value={assignedTo}
            onChange={setAssignedTo}
            allowClear
            filterOption={(inputValue, option) => (
              String(option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
            )}
          />
        </div>

        {requiresOnHoldReason && (
          <div className="UpdateDelayReportModal__Field">
            <label>
              On-Hold reason
              <span className="QCFormSection__RequiredMark"> *</span>
            </label>
            <TextArea
              rows={2}
              value={onHoldReason}
              onChange={(event) => setOnHoldReason(event.target.value)}
              placeholder="Why is this RCA on hold?"
            />
          </div>
        )}

        <div className="UpdateDelayReportModal__Field">
          <label className="MarkSampleQCModal__Label MarkSampleQCModal__Label--block">
            Expected RCA Completion Date
            <span className="QCFormSection__RequiredMark"> *</span>
          </label>
          <div className="QCFormSection__RcaActionsRow">
            <DatePicker
              className="QCFormSection__ExpectedCompleteDate"
              value={expectedCompleteDate}
              onChange={setExpectedCompleteDate}
              format="MM/DD/YYYY"
              allowClear={false}
              disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
            />
            <Upload
              accept={RCA_UPLOAD_ACCEPT}
              multiple
              showUploadList={false}
              beforeUpload={beforeUpload}
              customRequest={customUpload}
              disabled={uploadingCount > 0}
            >
              <Button
                type="default"
                icon={<PaperClipOutlined />}
                loading={uploadingCount > 0}
              >
                Attach Document
                {requiresCompletedFields && (
                  <span className="QCFormSection__RequiredMark"> *</span>
                )}
              </Button>
            </Upload>
          </div>
        </div>

        {displayedS3Keys.length > 0 && (
          <div className="UpdateDelayReportModal__Field">
            <div className="QCFormSection__RcaSectionTitle">Attached Documents</div>
            <RcaDocuments
              s3Keys={displayedS3Keys}
              removableS3Keys={displayedS3Keys}
              onRemove={removeRcaDocument}
            />
          </div>
        )}

        <RcaRelatedWorkorderTable
          rows={relatedWorkorderRows}
          loading={relatedWorkordersLoading}
        />
      </div>
    </Modal>
  );
}
